import {
  useAssetType,
  useFacilitiesList,
  useFacilityTypeOptions,
  useNetworkChoices,
  useNetworkOptions,
  useSystemOptions,
} from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import React, { useEffect, useMemo, useState } from "react";

interface EntityFilters {
  system?: string;
  facility?: string;
  type?: string;
  network?: string;
}

interface EntityFilterPanelProps {
  currentFilters: EntityFilters;
  onApplyFilters: (filters: Partial<EntityFilters>) => void;
  onResetFilters: () => void;
  serviceAssociation: string;
}

export const EntityFilterPanel: React.FC<EntityFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
  serviceAssociation,
}) => {
  // Local state for temporary filter values
  const [tempFilters, setTempFilters] = useState<EntityFilters>(currentFilters);

  const { data: assetTypeList } = useAssetType();
  const typeOptions = useMemo(
    () =>
      assetTypeList?.result?.map((t: any) => ({
        value: t.code,
        label: t.name,
      })) || [],
    [assetTypeList]
  );

  const { data: networkTypeData } = useNetworkChoices();

  const { data: facilityTypeData } = useFacilityTypeOptions();

  const networkTypeOptions = useMemo(
    () =>
      networkTypeData?.result?.map((data: any) => ({
        value: data.key,
        label: data.value,
      })) || [],
    [networkTypeData]
  );

  const facilityTypeOptions = useMemo(
    () =>
      facilityTypeData?.map((data: any) => ({
        value: data.code,
        label: data.name,
      })) || [],
    [facilityTypeData]
  );

  const { data: facilityList } = useFacilitiesList();
  const facilityOptions = useMemo(
    () =>
      facilityList?.map((f: any) => ({
        value: f.id,
        label: f.facilityName,
      })) || [],
    [facilityList]
  );

  const { data: systemList } = useSystemOptions();
  const systemOptions = useMemo(
    () =>
      systemList?.result?.map((s: any) => ({
        value: s.id,
        label: s.systemNo,
      })) || [],
    [systemList]
  );

  const { data: networkList } = useNetworkOptions();
  const networkOptions = useMemo(
    () =>
      networkList?.result?.map((n: any) => ({
        value: n.id,
        label: n.networkName,
      })) || [],
    [networkList]
  );

  const getFieldsForAssociation = (): FormField[] => {
    const fields: FormField[] = [];

    switch (serviceAssociation) {
      case "Asset":
        fields.push(
          {
            name: "system",
            type: "select",
            label: "System",
            options: systemOptions,
            placeholder: "Select system",
            classes: FIELD_CLASSES,
          },
          {
            name: "facility",
            type: "select",
            label: "Facility",
            options: facilityOptions,
            placeholder: "Select facility",
            classes: FIELD_CLASSES,
          },
          {
            name: "type",
            type: "select",
            label: "Type",
            options: typeOptions,
            placeholder: "Select type",
            classes: FIELD_CLASSES,
          },
          {
            name: "network",
            type: "select",
            label: "Network",
            options: networkOptions,
            placeholder: "Select network",
            classes: FIELD_CLASSES,
          }
        );
        break;
      case "Facility":
        fields.push({
          name: "type",
          type: "select",
          label: "Type",
          options: facilityTypeOptions,
          placeholder: "Select type",
          classes: FIELD_CLASSES,
        });
        break;
      case "System":
        fields.push(
          {
            name: "facility",
            type: "select",
            label: "Facility",
            options: facilityOptions,
            placeholder: "Select facility",
            classes: FIELD_CLASSES,
          },
          {
            name: "type",
            type: "select",
            label: "Type",
            options: typeOptions,
            placeholder: "Select type",
            classes: FIELD_CLASSES,
          }
        );
        break;
      case "Network":
        fields.push(
          {
            name: "facility",
            type: "select",
            label: "Facility",
            options: facilityOptions,
            placeholder: "Select facility",
            classes: FIELD_CLASSES,
          },
          {
            name: "type",
            type: "select",
            label: "Type",
            options: networkTypeOptions,
            placeholder: "Select type",
            classes: FIELD_CLASSES,
          }
        );
        break;
    }

    return fields;
  };

  const form = FormService.createForm({
    fields: getFieldsForAssociation(),
    defaultValues: tempFilters,
  });

  // Reset on serviceAssociation change
  useEffect(() => {
    form.reset(tempFilters);
  }, [serviceAssociation]);

  // Handle form submission - PREVENT DEFAULT BEHAVIOR
  const handleSubmit = (data: any, event?: React.FormEvent) => {
    // Prevent form submission from reloading the page
    if (event) {
      event.preventDefault();
    }
    onApplyFilters(data);
  };

  // Handle apply button click - PREVENT DEFAULT AND TRIGGER APPLY
  const handleApplyClick = (event: React.MouseEvent) => {
    event.preventDefault(); // Prevent form submission
    const currentFormData = form.getValues();
    handleSubmit(currentFormData);
  };

  // Handle reset
  const handleReset = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault(); // Prevent any default behavior
    }

    setTempFilters({});
    form.reset({});
    onResetFilters();
  };

  return (
    <DynamicForm
      fields={getFieldsForAssociation()}
      form={form}
      onSubmit={handleSubmit}
      onReset={handleReset}
      onFieldChange={(name, value) =>
        setTempFilters((prev) => ({ ...prev, [name]: value }))
      }
      gridColumns={4}
      config={{
        showSubmitButton: false,
        showResetButton: false,
        layout: "horizontal",
        spacing: "medium",
      }}
      className="space-y-6"
      renderCustomFooter={() => (
        <div className="col-span-1 md:col-span-2 lg:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleReset}>
            Reset Filters
          </Button>
          <Button
            type="button" // Changed from "submit" to "button"
            onClick={handleApplyClick} // Use custom click handler
          >
            Apply Filters
          </Button>
        </div>
      )}
    />
  );
};
